import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { useCancelarPedidoAguardandoPagamentoMutation, type StatusPedidoPublico } from '../../api';
import type { RootState } from '../../store';
import { descartarEnvioPedido } from '../../store/carrinho-slice';
import { formatarBRL, textoFaltaParaPagar } from '../../lib/formato';
import { useAcaoUnica } from '../../lib/use-acao-unica';
import { DialogPagamentoOnline } from '../../components/dialog-pagamento-online';
import { DialogCancelarPagamento } from '../../components/dialog-cancelar-pagamento';

const AVISO_SEM_CANCELAR =
  'Não deu para cancelar agora. Se você não pagar, o pedido é cancelado sozinho quando o tempo acabar.';

/** Status da cobrança que já liberam o pedido (o mesmo recorte do backend). */
const STATUS_PAGOS = ['AUTHORIZED', 'CONFIRMED', 'RECEIVED'];

/** Delivery com pagamento online não pago: antes caía em "Pedido confirmado" com check verde (#3548). */
export function AguardandoPagamento({
  pedido,
  referencia,
}: {
  pedido: StatusPedidoPublico;
  referencia?: string;
}): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const temCarrinho = useSelector((s: RootState) => s.carrinho.itens.length > 0);
  const [cancelarPedido] = useCancelarPedidoAguardandoPagamentoMutation();

  const [pagando, setPagando] = useState(false);
  const [confirmandoCancelamento, setConfirmandoCancelamento] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  const total = pedido.payloadJson?.valorTotal ?? 0;
  const metodo = pedido.metodoPagamentoOnline ?? null;
  // Pago e ainda indo para a loja: o backend já não oferece pagar, mas "não dá para pagar" seria falso.
  const jaPago = STATUS_PAGOS.includes(pedido.statusPagamento ?? '');

  const expiraMs = pedido.expiraPagamentoEm ? new Date(pedido.expiraPagamentoEm).getTime() : null;
  const [agoraMs, setAgoraMs] = useState(() => Date.now());
  useEffect(() => {
    if (!expiraMs) return undefined;
    const t = window.setInterval(() => setAgoraMs(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [expiraMs]);
  const restanteSeg = expiraMs ? Math.max(0, Math.round((expiraMs - agoraMs) / 1000)) : null;
  const prazoEsgotado = restanteSeg === 0;

  const podePagar = metodo != null && total > 0 && !prazoEsgotado;

  /** 'JA_PAGO': o pagamento chegou no meio do caminho — a tela se atualiza sozinha. */
  async function cancelarNoBackend(): Promise<'CANCELADO' | 'JA_PAGO' | 'FALHOU'> {
    try {
      await cancelarPedido(pedido.id).unwrap();
      return 'CANCELADO';
    } catch (err) {
      const codigo = (err as { data?: { codigo?: string } } | null)?.data?.codigo;
      return codigo === 'PEDIDO_NAO_AGUARDA_PAGAMENTO' ? 'JA_PAGO' : 'FALHOU';
    }
  }

  /** O aviso vai no estado da navegação: sobrevive à troca desta tela pela do pedido pago. */
  function avisarQueJaFoiPago(): void {
    navigate(`/pedido/${pedido.id}`, { replace: true, state: { pagoAoCancelar: true } });
  }

  const [confirmarCancelamento, cancelando] = useAcaoUnica(async () => {
    const resultado = await cancelarNoBackend();
    setConfirmandoCancelamento(false);
    setPagando(false);
    if (resultado === 'FALHOU') {
      setAviso(AVISO_SEM_CANCELAR);
      return;
    }
    if (resultado === 'JA_PAGO') {
      avisarQueJaFoiPago();
      return;
    }
    // O carrinho fica (pode não ser o deste pedido); só a chave do envio deixa de valer.
    dispatch(descartarEnvioPedido());
  });

  const [trocarForma, trocando] = useAcaoUnica(async () => {
    const resultado = await cancelarNoBackend();
    if (resultado === 'JA_PAGO') {
      setPagando(false);
      avisarQueJaFoiPago();
      return;
    }
    // Falha de rede não prende o cliente: ele refaz com outra forma e este expira no prazo.
    dispatch(descartarEnvioPedido());
    navigate('/checkout');
  });

  const ocupado = cancelando || trocando;

  return (
    <Stack spacing={2.5} sx={{ textAlign: 'center' }}>
      <Stack alignItems="center" spacing={1} sx={{ pt: 1 }}>
        <Box
          aria-hidden
          sx={(t) => ({
            width: 88,
            height: 88,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'warning.main',
            bgcolor: alpha(t.palette.warning.main, 0.12),
          })}
        >
          <HourglassTopIcon sx={{ fontSize: 44 }} />
        </Box>
        <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.25 }}>
          {prazoEsgotado ? 'O tempo para pagar acabou' : 'Aguardando pagamento'}
        </Typography>
        <Typography color="text.secondary" sx={{ maxWidth: 360 }}>
          {prazoEsgotado
            ? 'Este pedido não vai para a loja.'
            : 'Seu pedido só vai para a loja depois que o pagamento for aprovado.'}
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary">
        Pedido #{referencia}
        {total > 0 ? ` · ${formatarBRL(total)}` : ''}
      </Typography>

      {!prazoEsgotado &&
        (metodo == null ? (
          !jaPago && (
            <Typography color="text.secondary">
              Não dá para pagar este pedido por aqui. Se não for pago, ele é cancelado sozinho.
            </Typography>
          )
        ) : (
          restanteSeg != null && (
            <Typography variant="h6" fontWeight={700} color={restanteSeg <= 60 ? 'error.main' : 'text.primary'}>
              {textoFaltaParaPagar(restanteSeg)}
            </Typography>
          )
        ))}

      {aviso && (
        <Alert severity="info" sx={{ textAlign: 'left' }}>
          {aviso}
        </Alert>
      )}

      <Stack spacing={1.5}>
        {podePagar && (
          <Button variant="contained" size="large" onClick={() => setPagando(true)} disabled={ocupado}>
            Pagar agora
          </Button>
        )}
        {temCarrinho &&
          (prazoEsgotado ? (
            <Button variant="contained" size="large" onClick={() => void trocarForma()} disabled={ocupado}>
              Fazer o pedido de novo
            </Button>
          ) : (
            <Button variant="outlined" size="large" onClick={() => void trocarForma()} disabled={ocupado}>
              Trocar forma de pagamento
            </Button>
          ))}
        {!prazoEsgotado && (
          <Button color="error" onClick={() => setConfirmandoCancelamento(true)} disabled={ocupado}>
            Cancelar pedido
          </Button>
        )}
      </Stack>

      {pagando && metodo && (
        <DialogPagamentoOnline
          aberto
          pedidoId={pedido.id}
          valorCentavos={Math.round(total * 100)}
          metodos={[metodo]}
          onSucesso={() => setPagando(false)}
          onCancelar={() => setConfirmandoCancelamento(true)}
          onRefazer={() => setPagando(false)}
          onFechar={() => setPagando(false)}
          {...(temCarrinho ? { onOutraForma: () => void trocarForma() } : {})}
          ocupado={ocupado}
        />
      )}

      <DialogCancelarPagamento
        aberto={confirmandoCancelamento}
        titulo="Cancelar o pedido?"
        descricao="O pedido é cancelado e não vai para a loja."
        cancelando={cancelando}
        onVoltar={() => setConfirmandoCancelamento(false)}
        onConfirmar={() => void confirmarCancelamento()}
      />
    </Stack>
  );
}

export default AguardandoPagamento;
